import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { DocumentSummary, HealthDocumentKind } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

/** Tab names in tab order; every kind must be here (the type checks it). */
const KIND_LABELS: Record<HealthDocumentKind, string> = {
  lab_order: 'Направления на анализы',
  test_result: 'Результаты обследований',
  invoice: 'Счета и сметы',
  doctor_report: 'Заключения врачей',
  prescription: 'Назначения',
  referral: 'Направления к врачам',
  imaging: 'УЗИ, МРТ, снимки',
  discharge: 'Выписки',
  certificate: 'Справки',
  insurance: 'Страховка',
  other: 'Другое',
};
const KINDS = Object.keys(KIND_LABELS) as HealthDocumentKind[];

function formatDay(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'long' }).format(date);
}

function formatAmount(amount: string | null, currency: string | null): string | undefined {
  if (amount === null || !currency) return undefined;
  const value = Number(amount);
  if (Number.isNaN(value)) return `${amount} ${currency}`;
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).format(value);
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1_048_576) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1_048_576).toFixed(1)} МБ`;
}

function author(label: string | null): string | undefined {
  return label?.replace(/^(user|local):/, '');
}

function meta(d: DocumentSummary): string {
  return [
    formatDay(d.date),
    d.issuer ?? undefined,
    d.author ?? undefined,
    formatAmount(d.amount, d.currency),
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The whole document, opened from the list. */
function DocumentCard({
  id,
  canWrite,
  filesSupported,
  onClose,
}: {
  id: number;
  canWrite: boolean;
  filesSupported: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['documents', id], queryFn: () => api.document(id) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['documents'] });
  const remove = useMutation({
    mutationFn: () => api.deleteDocument(id),
    onSuccess: () => {
      onClose();
      void invalidate();
    },
  });
  const attach = useMutation({
    mutationFn: (file: File) => api.attachDocumentFile(id, file),
    onSuccess: () => void invalidate(),
  });
  const detach = useMutation({
    mutationFn: () => api.detachDocumentFile(id),
    onSuccess: () => void invalidate(),
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const d = query.data?.document;
  return (
    <article className="card document">
      <div className="card__header">
        <h3>{d?.title ?? 'Документ'}</h3>
        <div className="actions-row">
          {canWrite && d && filesSupported && (
            <>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.txt,.md,.doc,.docx,.xls,.xlsx,.zip"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) attach.mutate(file);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="btn btn--small btn--ghost"
                disabled={attach.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {attach.isPending ? 'Загружаю…' : d.file ? 'Заменить файл' : 'Прикрепить файл'}
              </button>
            </>
          )}
          {canWrite && d && (
            <button
              type="button"
              className="btn btn--small btn--ghost"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Удалить «${d.title}»? Это навсегда и для всех.`)) {
                  remove.mutate();
                }
              }}
            >
              Удалить
            </button>
          )}
          <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {remove.isError && <p className="text-error small">{remove.error.message}</p>}
      {attach.isError && <p className="text-error small">{attach.error.message}</p>}
      {detach.isError && <p className="text-error small">{detach.error.message}</p>}
      {d && (
        <>
          <p className="muted small">
            {KIND_LABELS[d.kind]} · {meta(d)}
            {d.labReportId && ` · анализы ${d.labReportId}`}
            {author(d.createdBy) && ` · добавил ${author(d.createdBy)}`}
            {` · ${formatShortDateTime(d.createdAt)}`}
          </p>
          {d.tags.length > 0 && (
            <p className="document__tags">
              {d.tags.map((tag) => (
                <span key={tag} className="document__tag">
                  {tag}
                </span>
              ))}
            </p>
          )}
          {d.file && (
            <p className="document__file">
              <a href={api.documentFileUrl(d.id)} target="_blank" rel="noreferrer">
                {d.file.name}
              </a>{' '}
              <span className="muted small">({formatBytes(d.file.bytes)})</span>
              {canWrite && d.text && (
                <button
                  type="button"
                  className="btn btn--small btn--ghost"
                  disabled={detach.isPending}
                  onClick={() => {
                    if (window.confirm('Убрать файл? Текст останется.')) detach.mutate();
                  }}
                >
                  Убрать файл
                </button>
              )}
            </p>
          )}
          {d.summary && (
            <p className="document__summary">
              <strong>Кратко.</strong> {d.summary}
            </p>
          )}
          {d.text ? (
            <div className="document__text markdown">
              <Markdown remarkPlugins={[remarkGfm]}>{d.text}</Markdown>
            </div>
          ) : (
            <p className="muted small">Текста нет: только файл.</p>
          )}
        </>
      )}
    </article>
  );
}

/** Adding a document by hand; the agent does the same with add_document. */
function AddDocumentForm({
  filesSupported,
  onAdded,
}: {
  filesSupported: boolean;
  onAdded: (id: number) => void;
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<HealthDocumentKind>('doctor_report');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [issuer, setIssuer] = useState('');
  const [docAuthor, setDocAuthor] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [tags, setTags] = useState('');
  const [summary, setSummary] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | undefined>();
  const [fileWarning, setFileWarning] = useState<string | undefined>();
  const add = useMutation({
    mutationFn: async () => {
      const amountValue = amount.trim() ? Number(amount.replace(',', '.')) : undefined;
      const { document } = await api.addDocument({
        kind,
        title: title.trim(),
        date,
        ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
        ...(docAuthor.trim() ? { author: docAuthor.trim() } : {}),
        ...(amountValue !== undefined && !Number.isNaN(amountValue)
          ? { amount: amountValue, currency: currency.trim().toUpperCase() }
          : {}),
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
        ...(text.trim() ? { text: text.trim() } : {}),
      });
      if (file) {
        try {
          await api.attachDocumentFile(document.id, file);
        } catch (err) {
          setFileWarning(
            `Документ сохранён, но файл не прикрепился: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      return document;
    },
    onSuccess: (document) => {
      setTitle('');
      setSummary('');
      setText('');
      setFile(undefined);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      onAdded(document.id);
    },
  });
  const ready =
    title.trim().length > 0 && date.length > 0 && (text.trim().length > 0 || file !== undefined);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFileWarning(undefined);
    if (ready) add.mutate();
  };
  return (
    <form className="card form form--wide" onSubmit={submit}>
      <div className="card__header">
        <h3>Добавить документ</h3>
      </div>
      <label>
        Название
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          required
          placeholder="Заключение эндокринолога"
        />
      </label>
      <div className="form__row">
        <label>
          Вид
          <select value={kind} onChange={(e) => setKind(e.target.value as HealthDocumentKind)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Дата документа
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
      </div>
      <div className="form__row">
        <label>
          Клиника / лаборатория
          <input
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            maxLength={200}
            placeholder="Laboratorio Echevarne"
          />
        </label>
        <label>
          Врач
          <input
            value={docAuthor}
            onChange={(e) => setDocAuthor(e.target.value)}
            maxLength={200}
            placeholder="Dr. García"
          />
        </label>
      </div>
      <div className="form__row">
        <label>
          Сумма <span className="muted small">(для счетов)</span>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="120.50"
          />
        </label>
        <label>
          Валюта
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            maxLength={3}
            placeholder="EUR"
          />
        </label>
        <label>
          Теги <span className="muted small">(через запятую)</span>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="щитовидка, эндокринолог"
          />
        </label>
      </div>
      <label>
        Кратко <span className="muted small">(необязательно)</span>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={2}
          maxLength={4000}
        />
      </label>
      <label>
        Текст документа{' '}
        <span className="muted small">(Markdown; можно оставить пустым, если есть файл)</span>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} />
      </label>
      {filesSupported ? (
        <label>
          Файл оригинала <span className="muted small">(PDF, фото, zip, до 50 МБ)</span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.txt,.md,.doc,.docx,.xls,.xlsx,.zip"
            onChange={(e) => setFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <p className="muted small">Файлы оригиналов не хранятся: хранилище S3 не настроено.</p>
      )}
      {add.isError && <p className="text-error small">{add.error.message}</p>}
      {fileWarning && <p className="text-error small">{fileWarning}</p>}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={!ready || add.isPending}>
          {add.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}

/**
 * Health documents: requests for tests, invoices, doctors' reports, newest first, by kind, with
 * a word search. Everyone reads; adding and deleting need the right `documents:write` (the
 * owner gives it in Settings → Users) or the owner role.
 */
export function Documents() {
  const [kind, setKind] = useState<HealthDocumentKind | undefined>();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<number | undefined>();
  const [adding, setAdding] = useState(false);
  const list = useQuery({
    queryKey: ['documents', 'list', kind ?? '', query],
    queryFn: () =>
      api.documents({ ...(kind ? { kind } : {}), ...(query ? { query } : {}), limit: 100 }),
  });
  const items = list.data?.documents ?? [];
  const canWrite = list.data?.canWrite ?? false;
  const filesSupported = list.data?.filesSupported ?? false;

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setQuery(search.trim());
  };

  return (
    <div className="page">
      <div className="page__header">
        <h2>Документы</h2>
        <div className="actions-row">
          <form className="search" onSubmit={submitSearch}>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по словам"
              aria-label="Поиск по документам"
            />
            <button type="submit" className="btn btn--small">
              Найти
            </button>
          </form>
          {canWrite && (
            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={() => setAdding((v) => !v)}
            >
              {adding ? 'Скрыть форму' : 'Добавить'}
            </button>
          )}
        </div>
      </div>
      <p className="muted">
        Медицинские документы: направления на анализы, счета, заключения врачей, назначения,
        выписки. Читают все; добавлять и удалять могут владелец и те, кому он выдал право в
        Настройках → Пользователи.
      </p>
      {adding && canWrite && (
        <AddDocumentForm
          filesSupported={filesSupported}
          onAdded={(id) => {
            setAdding(false);
            setOpen(id);
          }}
        />
      )}
      <div className="tabs" role="tablist" aria-label="Виды документов">
        <button
          type="button"
          role="tab"
          aria-selected={kind === undefined}
          className={kind === undefined ? 'active' : undefined}
          onClick={() => setKind(undefined)}
        >
          Все
        </button>
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={k === kind}
            className={k === kind ? 'active' : undefined}
            onClick={() => setKind(k)}
          >
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>
      {open !== undefined && (
        <DocumentCard
          id={open}
          canWrite={canWrite}
          filesSupported={filesSupported}
          onClose={() => setOpen(undefined)}
        />
      )}
      {list.isPending && <p className="muted">Загрузка…</p>}
      {list.isError && <p className="text-error">{list.error.message}</p>}
      {list.data && !list.data.available && (
        <p className="muted">Документы недоступны: нет базы.</p>
      )}
      {list.data?.available && items.length === 0 && (
        <p className="muted">
          {query
            ? 'Ничего не найдено.'
            : 'Документов пока нет. Пришлите агенту PDF или фото и попросите сохранить.'}
        </p>
      )}
      {items.length > 0 && (
        <ul className="card page-list">
          {items.map((d) => (
            <li key={d.id} className="page-row">
              <div className="page-row__main">
                <button
                  type="button"
                  className="link-button page-row__title"
                  onClick={() => setOpen(d.id)}
                >
                  {d.title}
                </button>
                <span className="muted small">
                  {KIND_LABELS[d.kind]} · {meta(d)}
                  {d.file && ' · 📎'}
                </span>
                <span className="small transcript__excerpt">
                  {d.snippet ?? d.summary ?? d.excerpt}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {list.data && list.data.total > items.length && (
        <p className="muted small">
          Показаны {items.length} из {list.data.total}; уточните поиск.
        </p>
      )}
    </div>
  );
}
